import api from "@/lib/api";

export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;
const PARALLEL = 3;
const MAX_TRIES = 8;

const keyFor = (f) => `rf_up_${f.name}_${f.size}_${f.lastModified}`;
export const forgetUpload = (f) => localStorage.removeItem(keyFor(f));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const waitOnline = () => (navigator.onLine ? Promise.resolve() : new Promise((r) => window.addEventListener("online", r, { once: true })));

async function sendPiece(id, i, blob, onBytes, onNote) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      await api.put(`/import/chunked/${id}/${i}`, blob, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => onBytes(e.loaded),
      });
      onNote("");
      return;
    } catch (e) {
      onBytes(0);
      if ((e.response && e.response.status < 500) || attempt >= MAX_TRIES) throw e;
      onNote(navigator.onLine ? "Connection hiccup — retrying…" : "Connection lost — waiting to reconnect…");
      await waitOnline();
      await sleep(Math.min(30000, 1000 * 2 ** attempt));
    }
  }
}

async function resumeInfo(file) {
  const id = localStorage.getItem(keyFor(file));
  if (!id) return null;
  try {
    const { data } = await api.get(`/import/chunked/${id}`);
    if (data.finished || data.size !== file.size) return null;
    return { upload_id: id, chunk_size: data.chunk_size, total_chunks: data.total_chunks, received: data.received };
  } catch { return null; }
}

// Uploads one file in pieces (skipping any already sent earlier). Reports bytes sent; returns the upload id.
export async function uploadPieces(file, onBytes, onNote = () => {}) {
  let info = await resumeInfo(file);
  if (info?.received.length) onNote(`Carrying on — ${info.received.length} of ${info.total_chunks} pieces were already uploaded`);
  if (!info) {
    const { data } = await api.post("/import/chunked/start", { filename: file.name, size: file.size });
    info = { ...data, received: [] };
    localStorage.setItem(keyFor(file), data.upload_id);
  }
  const { upload_id: id, chunk_size: size, total_chunks: total } = info;
  const pieceLen = (i) => Math.min(file.size, (i + 1) * size) - i * size;
  const done = new Set(info.received);
  const loaded = Array.from({ length: total }, (_, i) => (done.has(i) ? pieceLen(i) : 0));
  const report = () => onBytes(loaded.reduce((a, b) => a + b, 0));
  report();
  const queue = [...Array(total).keys()].filter((i) => !done.has(i));
  const worker = async () => {
    while (queue.length) {
      const i = queue.shift();
      await sendPiece(id, i, file.slice(i * size, i * size + pieceLen(i)), (b) => { loaded[i] = b; report(); }, onNote);
      loaded[i] = pieceLen(i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
  return id;
}

export async function chunkedUpload(file, onPct, onNote) {
  const id = await uploadPieces(file, (b) => onPct(Math.min(99, Math.floor((b / file.size) * 100))), onNote);
  onPct(100);
  const { data } = await api.post(`/import/chunked/${id}/finish`);
  forgetUpload(file);
  return data;
}

export async function evernoteUpload(files, filter, onPct, onNote) {
  const total = files.reduce((a, f) => a + f.size, 0);
  let before = 0;
  const ids = [];
  for (const f of files) {
    ids.push(await uploadPieces(f, (b) => onPct(Math.min(99, Math.floor(((before + b) / total) * 100))), onNote));
    before += f.size;
  }
  onPct(100);
  const { data } = await api.post("/import/chunked/finish-evernote", { upload_ids: ids, ...(filter || {}) });
  files.forEach(forgetUpload);
  return data;
}
